/**
 * WorkspaceSwitcher — iter-438k Phase 5 (Unified Adaptive Dashboard).
 * ═══════════════════════════════════════════════════════════════════
 * Persistent header dropdown that lets multi-role users hop between
 * their authorised workspaces (Church → School → Events → Security …)
 * without leaving their session.
 *
 * Data source: `GET /api/identity/my-workspaces` (same cache key used
 * by the Adaptive Home Dashboard — React Query dedupes automatically).
 *
 * "Current workspace" is inferred from the URL: the workspace whose
 * `route` is the longest prefix of `location.pathname` wins.
 */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as LucideIcons from 'lucide-react';

import { getToken } from '@/lib/authToken';
import { Button } from '@/components/ui/button';
import usePlatformResolve from '@/hooks/usePlatformResolve';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const Fallback = LucideIcons.LayoutGrid;

/** Pick the workspace whose `route` is the longest prefix of `pathname`. */
export function pickCurrentWorkspace(workspaces, pathname) {
  if (!workspaces?.length || !pathname) return null;
  let best = null;
  let bestLen = -1;
  for (const ws of workspaces) {
    const route = (ws.route || '').split('?')[0];
    if (!route) continue;
    // Exact match OR route+'/' prefix (avoids matching /admin against /administer)
    if (pathname === route || pathname.startsWith(`${route}/`)) {
      if (route.length > bestLen) {
        best = ws;
        bestLen = route.length;
      }
    }
  }
  return best;
}

/** Group workspaces by category (used by the dropdown sections). */
function groupByCategory(workspaces, categories) {
  const bucket = new Map((categories || []).map((c) => [c.key, { ...c, items: [] }]));
  for (const ws of workspaces || []) {
    if (bucket.has(ws.category)) bucket.get(ws.category).items.push(ws);
  }
  return Array.from(bucket.values()).filter((c) => c.items.length > 0);
}

export const WorkspaceSwitcher = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { data, isLoading } = useQuery({
    queryKey: ['identity-my-workspaces'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/identity/my-workspaces`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    staleTime: 30_000,
    // Never surface an error card in the header — silently hide.
    retry: 1,
  });

  // iter 450d — Platform Resolve as the identity-registry whitelist. When
  // it succeeds and isSuperAdmin=false, intersect the rich IAM catalog with
  // the Person's active memberships so the switcher is strictly tied to
  // the central Person record. Super-admins see everything (fallback to
  // the full IAM catalog).
  const platformResolve = usePlatformResolve();

  const rawWorkspaces = data?.workspaces || [];
  const workspaces = React.useMemo(() => {
    // Only intersect when platform-resolve loaded, user is NOT super-admin,
    // AND at least one workspace exists in the resolve response. Otherwise
    // fall back to the full IAM catalog (backwards-compatible).
    if (!platformResolve.data || platformResolve.isSuperAdmin) return rawWorkspaces;
    const allowedKeys = new Set((platformResolve.workspaces || []).map((w) => w.key));
    if (allowedKeys.size === 0) return rawWorkspaces;
    return rawWorkspaces.filter((ws) => allowedKeys.has(ws.key));
  }, [rawWorkspaces, platformResolve.data, platformResolve.isSuperAdmin, platformResolve.workspaces]);

  const categories = data?.categories || [];

  const current = React.useMemo(
    () => pickCurrentWorkspace(workspaces, location.pathname),
    [workspaces, location.pathname],
  );

  const grouped = React.useMemo(
    () => groupByCategory(workspaces, categories),
    [workspaces, categories],
  );

  // Nothing to switch to → don't render at all (avoids a dead affordance).
  if (isLoading) return null;
  if (workspaces.length <= 1) return null;

  const CurrentIcon = current ? (LucideIcons[current.icon] || Fallback) : LucideIcons.LayoutGrid;
  const currentColor = current?.color || '#64748b';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 h-9 max-w-[220px]"
          data-testid="workspace-switcher-trigger"
        >
          <CurrentIcon className="h-4 w-4 flex-shrink-0" style={{ color: currentColor }} />
          <span className="text-sm font-medium truncate">
            {current?.label || 'Home'}
          </span>
          <LucideIcons.ChevronsUpDown className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-72 max-h-[70vh] overflow-y-auto"
        data-testid="workspace-switcher-menu"
      >
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Switch workspace</span>
          <span className="text-xs font-normal text-slate-400">{workspaces.length} available</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Always-present Home shortcut */}
        <DropdownMenuItem
          onClick={() => navigate('/home')}
          data-testid="workspace-switcher-item-home"
        >
          <LucideIcons.Home className="h-4 w-4 mr-2 text-slate-500" />
          <span>Home</span>
          <span className="ml-auto text-xs text-slate-400">/home</span>
        </DropdownMenuItem>

        {grouped.map((cat, idx) => (
          <React.Fragment key={cat.key}>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs uppercase tracking-wide text-slate-500">
              {cat.label}
            </DropdownMenuLabel>
            {cat.items.map((ws) => {
              const Icon = LucideIcons[ws.icon] || Fallback;
              const isActive = current?.key === ws.key;
              return (
                <DropdownMenuItem
                  key={ws.key}
                  onClick={() => navigate(ws.route)}
                  className={isActive ? 'bg-slate-100 font-medium' : ''}
                  data-testid={`workspace-switcher-item-${ws.key}`}
                >
                  <Icon
                    className="h-4 w-4 mr-2 flex-shrink-0"
                    style={{ color: ws.color || '#64748b' }}
                  />
                  <span className="truncate">{ws.label}</span>
                  {isActive && (
                    <LucideIcons.Check
                      className="h-3.5 w-3.5 ml-auto text-emerald-600 flex-shrink-0"
                      data-testid={`workspace-switcher-active-${ws.key}`}
                    />
                  )}
                </DropdownMenuItem>
              );
            })}
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default WorkspaceSwitcher;
